import {
  getIconSVG,
  PplIconService,
  svgToDataURL
} from './icon.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input
} from '@angular/core';
import { Identity } from '@ppl/environment';
import { MemoizeLast } from '@ppl/utils/memoize.utils';
import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import type { Subscription } from 'rxjs';

export interface PplIconState {
  name: string;
  active: boolean;
}

const HOVER_VALUE = 'hover';
const STATE_DELIMITER = '_';

@Component({
  selector: 'ppl-icon',
  template: '',
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PplIconComponent implements OnInit, OnChanges, OnDestroy {
  @Input() svgIcon: string;
  @Input() hoverIcon: string;
  @Input() actionElement: Element;
  @Input() hoverEnabled = false;
  @Input() states: PplIconState[] = [];
  @Input() useSvg = false;
  @Input() svgStrip: { enabled: boolean, keepEmptyFill?: boolean; } = null;
  @Input() @HostBinding('style.--ppl-icon-color') color?: string;
  @Input() fill?: string;
  @Input() hoverFill?: string;

  @Input() width = '20px';
  @Input() height = '20px';
  @Input() legacyWidth?: string;
  @Input() legacyHeight?: string;

  private element: Element;
  private isHovered: boolean;
  private iconsLoadedSubscription: Subscription;

  private mouseEnterHandler: EventListener;
  private mouseLeaveHandler: EventListener;

  @MemoizeLast<PplIconComponent>(['svgIcon'])
  get isLucideIcon() {
    const resultIcon = this.iconService.icons[this.svgIcon];

    return resultIcon && resultIcon.innerHTML.includes('data-source="lucide"');
  }

  @HostBinding('style.fill')
  get fillValue() {
    if (this.useSvg && Identity === 'pipeliner') {
      return this.fill;
    }
  }

  @HostBinding('style.width')
  get widthValue() {
    if (this.legacyWidth && !this.isLucideIcon) {
      return this.legacyWidth;
    }

    return this.width;
  }

  @HostBinding('style.height')
  get heightValue() {
    if (this.legacyHeight && !this.isLucideIcon) {
      return this.legacyHeight;
    }

    return this.height;
  }

  constructor(public elementRef: ElementRef, private iconService: PplIconService, private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['svgIcon'] && !changes['svgIcon'].isFirstChange() && changes['svgIcon'].previousValue !== changes['svgIcon'].currentValue) ||
      (changes['fill'] && !changes['fill'].isFirstChange() && changes['fill'].previousValue !== changes['fill'].currentValue) ||
      (changes['states'] && this.hasStateChanged(changes['states'].previousValue, changes['states'].currentValue))
    ) {
      this.initStates();
    }

    if (changes.hoverEnabled && !changes.hoverEnabled.firstChange && changes.hoverEnabled.currentValue !== changes.hoverEnabled.previousValue) {
      if (changes.hoverEnabled.currentValue) {
        this.registerHoverListeners();
      } else {
        this.unregisterHoverListeners();
      }
    }
  }

  ngOnInit() {
    if (!this.iconService.isLoaded$.getValue()) {
      this.iconsLoadedSubscription = this.iconService.isLoaded$.subscribe(() => {
        this.initStates();
      });
    }

    this.element = this.actionElement || this.elementRef.nativeElement;
    this.initStates();

    if (this.hoverEnabled) {
      this.registerHoverListeners();
    }
  }

  ngOnDestroy() {
    if (this.hoverEnabled) {
      this.unregisterHoverListeners();
    }
    if (this.iconsLoadedSubscription) {
      this.iconsLoadedSubscription.unsubscribe();
    }
  }

  registerHoverListeners() {
    this.element.addEventListener('mouseenter', this.mouseEnterHandler = this.onMouseEnter.bind(this));
    this.element.addEventListener('mouseleave', this.mouseLeaveHandler = this.onMouseLeave.bind(this));
  }

  unregisterHoverListeners() {
    this.element.removeEventListener('mouseenter', this.mouseEnterHandler);
    this.element.removeEventListener('mouseleave', this.mouseLeaveHandler);
  }

  onMouseEnter() {
    this.isHovered = true;
    this.initStates();
  }

  onMouseLeave() {
    this.isHovered = false;
    this.initStates();
  }

  private hasStateChanged(previousValue: PplIconState[] | null, currentValue: PplIconState[] | null) {
    if (!currentValue || !previousValue) {
      return true;
    } else {
      return !!currentValue.find((state, index) => previousValue[index].name !== state.name || previousValue[index].active !== state.active);
    }
  }

  private initStates() {
    if (this.element) {
      let iconName = this.svgIcon;
      const iconStates = new Set();

      this.states.forEach((state) => {
        if (state.active) {
          iconStates.add(state.name);
        }
      });

      if (this.isHovered && !iconStates.has(HOVER_VALUE) && !this.hoverIcon) {
        iconStates.add(HOVER_VALUE);
      } else if (this.isHovered && this.hoverIcon) {
        iconName = this.hoverIcon;
      }

      if (iconStates.size > 0) {
        iconName = iconName + STATE_DELIMITER + Array.from(iconStates).join(STATE_DELIMITER);
      }

      const baseIcon = this.iconService.icons[this.svgIcon];
      const resultIcon = this.iconService.icons[iconName];
      const icon = resultIcon ? resultIcon : baseIcon ? baseIcon : null;

      if (icon) {
        if (!this.useSvg) {
          let url = icon.dataURL;

          if (icon.innerHTML.includes('data-source="lucide"')) {
            const defaultColor = icon.innerHTML.match(/data-default-color="(.*?)"/)?.[1] || 'var(--ppl-color-500)';

            const activeFill = this.isHovered ? (this.hoverFill || this.fill) : this.fill;
            let background = activeFill || defaultColor;

            if (localStorage.colorOverride === 'pipeliner' && TempVarColors[background]) {
              background = TempVarColors[background];
            }

            this.elementRef.nativeElement.style.mask = `url(${url}) no-repeat center`;
            this.elementRef.nativeElement.style.background = background;
          } else {
            this.elementRef.nativeElement.style.mask = '';
            this.elementRef.nativeElement.style.background = `url(${url}) no-repeat center`;
          }
          return;
        }

        this.elementRef.nativeElement.innerHTML = !!this.svgStrip?.enabled
          ? getIconSVG(icon.innerHTML, this.svgStrip.keepEmptyFill)
          : icon.innerHTML;
      }
    }
  }
}

// Fixme: just for colorOverride experiment, should be removed
const TempVarColors = {
  'var(--ppl-color-900)': '#06121f',
  'var(--ppl-color-800)': '#0c253e',
  'var(--ppl-color-700)': '#102e4e',
  'var(--ppl-color-600)': '#183c66',
  'var(--ppl-color-500)': '#005895',
  'var(--ppl-color-400)': '#4c7db0',
  'var(--ppl-color-300)': '#799dc4',
  'var(--ppl-color-200)': '#a5bed7',
  'var(--ppl-color-100)': '#d2deeb',
  'var(--ppl-color-50)': '#e3ebf3',
  'var(--ppl-color-25)': '#f6f8fb',
  'var(--ppl-teal-900)': '#01221e',
  'var(--ppl-teal-800)': '#02453b',
  'var(--ppl-teal-700)': '#036759',
  'var(--ppl-teal-600)': '#048a76',
  'var(--ppl-teal-500)': '#05ac94',
  'var(--ppl-teal-400)': '#37bda9',
  'var(--ppl-teal-300)': '#69cdbf',
  'var(--ppl-teal-200)': '#9bded4',
  'var(--ppl-teal-100)': '#cdeeea',
  'var(--ppl-teal-50)': '#e6f7f4',
  'var(--ppl-teal-25)': '#f2fbfa',
  'var(--ppl-red-900)': '#320b0b',
  'var(--ppl-red-800)': '#651515',
  'var(--ppl-red-700)': '#972020',
  'var(--ppl-red-600)': '#ca2a2a',
  'var(--ppl-red-500)': '#fc3535',
  'var(--ppl-red-400)': '#fd5d5d',
  'var(--ppl-red-300)': '#fd8686',
  'var(--ppl-red-200)': '#feaeae',
  'var(--ppl-red-100)': '#fed7d7',
  'var(--ppl-red-50)': '#ffebeb',
  'var(--ppl-red-25)': '#fff5f5',
  'var(--ppl-orange-900)': '#331600',
  'var(--ppl-orange-800)': '#662c00',
  'var(--ppl-orange-700)': '#994300',
  'var(--ppl-orange-600)': '#cc5900',
  'var(--ppl-orange-500)': '#ff6f00',
  'var(--ppl-orange-400)': '#ffa35c',
  'var(--ppl-orange-300)': '#ffa966',
  'var(--ppl-orange-200)': '#ffc599',
  'var(--ppl-orange-100)': '#ffe2cc',
  'var(--ppl-orange-50)': '#fff1e5',
  'var(--ppl-orange-2)5': '#fff8f2'
};
